import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, Loader2, Receipt, ShieldAlert } from 'lucide-react';
import { Button } from '../Button';
import { BookingSplitRow } from '../../types';
import { UnbookedSentReportRow, isValidExemptReason } from '../../services/unbookedReportsLogic';
import { RebookPreview, istNachbuchbar } from '../../services/reportRebookLogic';
import { RebookKontext, ladeRebookKontext, nachbuchen, rechneVorschauNeu } from '../../services/reportRebookService';
import { bookingIsFlatProof, FLAT_PROOF_HINT } from '../../services/flatRateLogic';

interface Props {
    row: UnbookedSentReportRow;
    /** Abrechnungslage aus `details.abrechnung` des Befundes; `null` = unbekannt. */
    abrechnung?: { stand?: string; beleg?: string; von?: string; bis?: string } | null;
    istSchreibgeschuetzt?: boolean;
    sperrHalter?: string | null;
    /** Der zweite Ausweg — „nicht abrechenbar" mit Pflichtgrund. */
    onMarkExempt: (reportHistoryId: string, grund: string) => Promise<void>;
    /** Wird nach einer erfolgreichen Buchung gerufen (Liste neu laden). */
    onDone: () => void;
    onClose: () => void;
}

const tagDeutsch = (ymd?: string | null) => (ymd ? ymd.split('-').reverse().join('.') : 'unbekannt');

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BEGEHUNG NACHBUCHEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der einzige Weg, einen bereits VERSENDETEN Bericht nachtraeglich abzurechnen.
 * Bis hierher gab es keinen: gebucht wird beim Versenden, ein zweiter Versand
 * bucht nicht nach, und die Leistungserfassung schreibt eine formlose Zeile,
 * die jede spaetere Pruefung uebersieht.
 *
 * Der Dialog ist absichtlich geschwaetzig. Er zeigt VOR dem Klick, was
 * geschrieben wird — Projekt, Bericht, Begehungstag, Zielposition, Menge,
 * Einheit, bei einer Tochter das Mutterprojekt — und vor allem die
 * ABRECHNUNGSLAGE. Liegt der Begehungstag in einem gestellten Zeitraum, ist
 * die richtige Antwort fast immer „nicht abrechenbar" und nicht eine zweite
 * Buchung: die Leistung wurde dann anders abgerechnet, und die Nachbuchung
 * waere eine Doppelforderung an einen Kunden, der den Beleg schon hat.
 *
 * Deshalb stehen beide Auswege nebeneinander in EINEM Fenster. Wer nur einen
 * Knopf „Buchen" anbietet, bekommt Buchungen.
 */
export const ReportRebookDialog: React.FC<Props> = ({
    row,
    abrechnung,
    istSchreibgeschuetzt,
    sperrHalter,
    onMarkExempt,
    onDone,
    onClose,
}) => {
    const [kontext, setKontext] = useState<RebookKontext | null>(null);
    const [ladefehler, setLadefehler] = useState<string | null>(null);
    const [zielId, setZielId] = useState<string>('');
    const [menge, setMenge] = useState<string>('');
    const [laeuft, setLaeuft] = useState(false);
    const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);
    const [exemptOffen, setExemptOffen] = useState(false);
    /** Gebucht — der Vorgang ist zu Ende, das Fenster zeigt nur noch das Ergebnis. */
    const [erledigt, setErledigt] = useState(false);
    const [grund, setGrund] = useState('');

    useEffect(() => {
        let abgebrochen = false;
        // Geladen wird allein anhand des Berichts. Schreibschutz, Sperrhalter
        // und Abrechnungslage sind Anzeigegroessen und fliessen ueber
        // `rechneVorschauNeu` ein — sonst laedt der Dialog jedes Mal neu vom
        // Server, wenn sich nur ein Hinweistext aendert.
        ladeRebookKontext(row)
            .then(k => {
                if (abgebrochen) return;
                setKontext(k);
                const erste = k.vorschlag.bookings[0];
                setZielId(erste?.serviceId || '');
                setMenge(erste ? String(erste.quantity) : '1');
            })
            .catch(e => { if (!abgebrochen) setLadefehler(e?.message || 'Unbekannter Fehler'); });
        return () => { abgebrochen = true; };
    }, [row]);

    /** Die Aufteilung, so wie sie der Bearbeiter gerade eingestellt hat. */
    const bookings: BookingSplitRow[] = useMemo(() => {
        // Mehrere Lose bleiben, wie das Archiv sie kennt — sie lassen sich hier
        // nicht umverteilen. Wer eine Aufteilung aendern will, tut das in der
        // Leistungserfassung; hier ginge sonst der Bezug zum Beleg verloren.
        if ((kontext?.vorschlag.bookings.length || 0) > 1) return kontext!.vorschlag.bookings;
        if (!zielId) return [];
        const zahl = Number((menge || '').replace(',', '.'));
        return [{ serviceId: zielId, quantity: Number.isFinite(zahl) && zahl > 0 ? zahl : 1 }];
    }, [kontext, zielId, menge]);

    const preview: RebookPreview | null = useMemo(
        () => (kontext ? rechneVorschauNeu(kontext, bookings, { istSchreibgeschuetzt, sperrHalter, abrechnung }) : null),
        [kontext, bookings, istSchreibgeschuetzt, sperrHalter, abrechnung],
    );

    const bereit = !!preview && istNachbuchbar(preview) && !laeuft && !erledigt;
    const zielIstStunden = useMemo(() => {
        const s = kontext?.services.find(x => x.id === zielId);
        // Eine Pauschale bucht einen Nachweis und keine Stunden — kein Mengenfeld.
        return !!s && !bookingIsFlatProof(s) && /std|stunde|^h$/i.test(s.unit || '');
    }, [kontext, zielId]);

    const buchen = async () => {
        if (!kontext || !preview) return;
        setLaeuft(true);
        setMeldung(null);
        const ergebnis = await nachbuchen({
            row,
            bookings,
            performerId: kontext.performerId,
            performerName: kontext.performerName,
            snapshot: kontext.snapshot,
            inspectionTime: kontext.inspectionTime,
            tochterName: kontext.tochterName,
        });
        // Jede uebersprungene Buchung wird angezeigt. `same_report` bleibt nur
        // dann draussen, wenn ueberhaupt nur EINE Position gebucht werden
        // sollte — dann ist es der erwartete Idempotenzfall und steht schon in
        // der Hauptmeldung. Bei einer AUFTEILUNG auf mehrere Lose ist derselbe
        // Hinweis dagegen die Nachricht „ein Los wurde nicht gebucht", und die
        // darf nicht verschwinden.
        const zeigen = ergebnis.skipped.filter(s => s.reason !== 'same_report' || bookings.length > 1);
        setMeldung({
            ok: ergebnis.ok,
            text: [ergebnis.message, ...zeigen.map(s => s.message)].filter(Boolean).join('\n\n'),
        });
        setLaeuft(false);
        // Die Liste wird aufgefrischt, das Fenster bleibt OFFEN: Erfolgsmeldung
        // und uebersprungene Buchungen sind das Ergebnis des Vorgangs. Wer sie
        // im selben Moment wegblendet, hat sie ebenso gut verschluckt.
        if (ergebnis.ok) { setErledigt(true); onDone(); }
    };

    const alsNichtAbrechenbar = async () => {
        if (!isValidExemptReason(grund)) return;
        setLaeuft(true);
        setMeldung(null);
        try {
            await onMarkExempt(row.report_history_id, grund.trim());
            onClose();
        } catch (e: any) {
            setMeldung({ ok: false, text: `Die Ausnahme konnte nicht gespeichert werden: ${e?.message || 'Unbekannter Fehler'}` });
            setLaeuft(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
                <div className="p-5 border-b border-slate-100 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                        <Receipt className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-slate-800">Begehung nachbuchen</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Bericht #{row.inspection_number} · {row.project_number || '—'} · {row.project_name || ''}
                        </p>
                    </div>
                </div>

                <div className="p-5 space-y-4">
                    {!kontext && !ladefehler && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Stand wird frisch vom Server geladen …
                        </div>
                    )}

                    {ladefehler && (
                        <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
                            {ladefehler}
                        </div>
                    )}

                    {preview && (
                        <>
                            {/* Was geschrieben wird — vollstaendig, vor dem Klick. */}
                            <dl className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm grid grid-cols-3 gap-y-2">
                                <dt className="text-slate-500 col-span-1">Begehungstag</dt>
                                <dd className="col-span-2 font-semibold text-slate-800 tabular-nums">
                                    {tagDeutsch(preview.begehungstag)} · {preview.uhrzeit} Uhr
                                </dd>
                                <dt className="text-slate-500">Durchgeführt von</dt>
                                <dd className="col-span-2 text-slate-700">{kontext?.performerName}</dd>
                                {preview.mutterProjektId && (
                                    <>
                                        <dt className="text-slate-500">Gebucht im</dt>
                                        <dd className="col-span-2 text-slate-700">
                                            Mutterprojekt {kontext?.buchungsProjektName || preview.mutterProjektId}
                                        </dd>
                                    </>
                                )}
                                <dt className="text-slate-500">Abrechnungslage</dt>
                                <dd className="col-span-2 text-slate-700">
                                    {abrechnung?.stand === 'abgerechnet'
                                        ? `Zeitraum bereits abgerechnet${abrechnung.beleg ? ` (${abrechnung.beleg})` : ''}`
                                        : abrechnung?.stand === 'kein_beleg'
                                            ? 'Kein Beleg über diesen Zeitraum'
                                            : 'Unbekannt'}
                                </dd>
                            </dl>

                            {/* Zielposition */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">
                                    Zielposition
                                </label>
                                <p className="text-[11px] text-slate-500 mb-2">{kontext?.vorschlag.begruendung}</p>
                                {(kontext?.vorschlag.bookings.length || 0) > 1 ? (
                                    <div className="rounded-xl border border-slate-200 divide-y">
                                        {preview.zeilen.map(z => (
                                            <div key={z.serviceId} className="p-2.5 text-sm flex justify-between gap-3">
                                                <span className="text-slate-700 truncate">{z.lvPosition} · {z.titel}</span>
                                                <span className="font-semibold tabular-nums shrink-0">{z.menge} {z.einheit}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex gap-2">
                                        <select
                                            value={zielId}
                                            onChange={e => setZielId(e.target.value)}
                                            className="flex-1 min-w-0 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white"
                                        >
                                            <option value="">— keine Position gewählt —</option>
                                            {(kontext?.services || []).map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {s.lvPosition} · {s.title} ({bookingIsFlatProof(s) ? 'Nachweis' : s.unit})
                                                </option>
                                            ))}
                                        </select>
                                        {zielIstStunden && (
                                            <input
                                                value={menge}
                                                onChange={e => setMenge(e.target.value)}
                                                inputMode="decimal"
                                                className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm tabular-nums"
                                                aria-label="Stunden"
                                            />
                                        )}
                                    </div>
                                )}
                                {preview.zeilen.length > 0 && (
                                    <p className="mt-2 text-sm text-slate-700">
                                        Gebucht wird:{' '}
                                        <b className="tabular-nums">
                                            {/* Auf der Pauschale ist `menge` nur das Buchungssignal — „1 Monat" hiesse einen Monat Geld. */}
                                            {preview.zeilen.map(z => (z.nachweis ? 'Nachweis' : `${z.menge} ${z.einheit}`)).join(' + ')}
                                        </b>{' '}
                                        auf {preview.zeilen.map(z => z.lvPosition).join(', ')}
                                    </p>
                                )}
                                {preview.zeilen.some(z => z.nachweis) && (
                                    <p className="mt-1 text-[11px] text-indigo-700 font-medium">
                                        {FLAT_PROOF_HINT}
                                    </p>
                                )}
                            </div>

                            {preview.blocker.map((b, i) => (
                                <div key={i} className="rounded-xl border border-rose-300 bg-rose-50 p-3 flex gap-2 text-sm text-rose-800">
                                    <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{b.message}</span>
                                </div>
                            ))}

                            {preview.warnungen.map((w, i) => (
                                <div key={i} className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm text-amber-900">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{w.message}</span>
                                </div>
                            ))}

                            {meldung && (
                                <div className={`rounded-xl border p-3 flex gap-2 text-sm whitespace-pre-line ${
                                    meldung.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-300 bg-rose-50 text-rose-800'
                                }`}>
                                    {meldung.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
                                    <span>{meldung.text}</span>
                                </div>
                            )}

                            {/* Der zweite Ausweg, gleichrangig daneben. */}
                            {exemptOffen && (
                                <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide">
                                        Grund (Pflicht)
                                    </label>
                                    <p className="text-[11px] text-slate-500">
                                        Wird mit Zeitpunkt und Person am Bericht festgehalten. Beispiel:
                                        „gebucht als Bericht #84 im selben Projekt".
                                    </p>
                                    <textarea
                                        value={grund}
                                        onChange={e => setGrund(e.target.value)}
                                        rows={2}
                                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 flex flex-wrap gap-2 justify-end">
                    <Button variant="ghost" onClick={onClose} disabled={laeuft}>
                        {erledigt ? 'Schließen' : 'Abbrechen'}
                    </Button>
                    {!erledigt && (exemptOffen ? (
                        <Button
                            variant="danger"
                            icon={<Ban className="w-4 h-4" />}
                            loading={laeuft}
                            disabled={!isValidExemptReason(grund) || laeuft}
                            onClick={() => void alsNichtAbrechenbar()}
                        >
                            Nicht abrechenbar
                        </Button>
                    ) : (
                        <Button variant="secondary" icon={<Ban className="w-4 h-4" />} onClick={() => setExemptOffen(true)} disabled={laeuft}>
                            Nicht abrechenbar …
                        </Button>
                    ))}
                    {!erledigt && (
                        <Button
                            variant="primary"
                            icon={<Receipt className="w-4 h-4" />}
                            loading={laeuft}
                            disabled={!bereit || exemptOffen}
                            onClick={() => void buchen()}
                        >
                            Buchen
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
